import { trpc, queryClient } from "~/utils/trpc";
import { DataTable } from "@quake/shared-ui/components/dataTable/dataTable";
import { FilterFields } from "@quake/shared-ui/components/dataTable/dataTableFilter";
import { Button } from "@quake/shared-ui/components/ui/button";
import { useDebounce } from "@quake/shared-ui/hooks/useDebounce";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ColumnDef, ColumnFiltersState, ColumnSort } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { toast } from "@quake/shared-ui/lib/sonner";
import { Download, EditIcon, PlusIcon, Trash2, Upload } from "lucide-react";
import SettingsIcon from "lucide-react/dist/esm/icons/settings";
import { BulkManualAutomationSelector } from "~/components/bulk-manual-automation-selector";
import SearchIcon from "lucide-react/dist/esm/icons/search";
import { attrTypes, FilterType, columns_sort as columnsSort } from "@quake/isomorphic";
import { truncateString } from "@quake/isomorphic/string";
import z from "zod";
import { Input } from "@quake/shared-ui/components/ui/input";
import { applyStickyColumnPreference, reorderColumnsByAccessKeys } from "~/utils/reorderColumnsByAccessKeys";
import { useBreadcrumbsContext } from "~/contexts/BreadcrumbsContext";
import { DeleteMultipleRows } from "~/components/DeleteMultipleRows";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { Badge } from "@quake/shared-ui/components/ui/badge";
import { CompactBadgeList } from "@quake/shared-ui/components/ui/compact-badge-list";
import { inferOutput } from "@trpc/tanstack-react-query";
import { MultiRecordAttributeCell } from "~/components/multiAttributesCell";
import DOMPurify from "dompurify";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@quake/shared-ui/components/ui/hover-card";
import { markdownToString } from "@quake/shared-ui/lib/markdown-to-string";
import { HumanDateWithTooltip } from "@quake/shared-ui/components/ui/human-date-with-tooltip";
import dayjs from "dayjs";
import { TablePreferences } from "~/components/dataTable/TablePreferences";
import { getFilterFromRowFilters } from "~/utils/datatable";
import { computeAgeInYears } from "~/utils/ageInYears";
import { renderTextWithLinks } from "@quake/shared-ui/components/ui/render-text-with-links";
import { RecordBadge } from "~/components/recordBadge";

/** Resolves a Beneficiary { type, id } to a human-readable label in a table cell */
function BeneficiaryCell({
  value,
  linkUser = false,
}: {
  value:
    | { type: string; id: string; name?: string; user_dataset_id?: string; is_resolved?: boolean; linkable?: boolean }
    | null
    | undefined;
  linkUser?: boolean;
}) {
  const isUser = value?.type === "user_dataset" || value?.type === "user";
  const isAutomation = value?.type === "automation";
  const isAgent = value?.type === "ai_agent" || value?.type === "agent";
  const isSystem = value?.type === "system";
  const shouldFetchUser = isUser && !!value?.id && (value?.name === undefined || value?.is_resolved === undefined);

  const { data: user, isLoading: isLoadingUser } = useQuery({
    ...trpc.users.getDatasetUserById.queryOptions({ id: value?.id ?? "" }),
    enabled: shouldFetchUser,
  });

  const { data: automation, isLoading: isLoadingAutomation } = useQuery({
    ...trpc.automations.getById.queryOptions({ id: value?.id ?? "" }),
    enabled: isAutomation && !!value?.id,
  });

  const { data: agent, isLoading: isLoadingAgent } = useQuery({
    ...trpc.aiAgents.getById.queryOptions({ id: value?.id ?? "" }),
    enabled: isAgent && !!value?.id,
  });

  if (!value || !value.id) return <span className="text-muted-foreground text-xs">—</span>;

  if (isSystem) return <span className="text-sm">System</span>;

  if ((shouldFetchUser && isLoadingUser) || isLoadingAutomation || isLoadingAgent) {
    return <span className="text-muted-foreground text-xs">...</span>;
  }

  if (isUser) {
    return (
      <RecordBadge
        linkable={linkUser}
        record={{
          id: value.user_dataset_id || user?.id || value.id,
          source: "user",
          label: value.name || user?.display_name || "Former team member",
          icon: "UserIcon",
          is_resolved: value.is_resolved ?? Boolean(user),
          linkable: value.linkable ?? Boolean(user),
        }}
      />
    );
  }

  if (isAutomation) {
    return <span className="text-sm">{automation?.name || "Automation"}</span>;
  }

  if (isAgent) {
    return <span className="text-sm">{agent?.name || "AI Agent"}</span>;
  }

  // Fallback for unknown types
  const label = value.type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return <span className="text-sm">{label}</span>;
}

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/custom/details/$id/table/")({
  component: RouteComponent,
  loader: async ({ params: { id } }) => {
    const dataFiltersP = queryClient.fetchQuery(trpc.customEntities.filters.queryOptions({ id }));
    const tablePrefsP = queryClient.fetchQuery(trpc.users.getTablePreferences.queryOptions({ tableName: id }));

    const [dataFilters, tablePrefs] = await Promise.all([dataFiltersP, tablePrefsP]);

    const activePreference = tablePrefs.activePreferenceId
      ? tablePrefs.prefs.find((pref: any) => pref.id === tablePrefs.activePreferenceId)
      : null;

    return {
      initialDataFilters: (activePreference?.filters || dataFilters) as Record<string, FilterType>,
      baseDataFilters: dataFilters as Record<string, FilterType>,
      initialColumnsOrder: (activePreference?.columnsOrder || tablePrefs.defaultColumnsOrder || []) as string[],
      baseColumnsOrder: (tablePrefs.defaultColumnsOrder || []) as string[],
      initialColumnsDisplay: (activePreference?.columnsDisplay || tablePrefs.defaultColumnsDisplay || {}) as Record<
        string,
        boolean
      >,
      baseColumnsDisplay: (tablePrefs.defaultColumnsDisplay || {}) as Record<string, boolean>,
      activeSort: (activePreference?.sort || []) as ColumnSort[],
      activePreferenceId: tablePrefs.activePreferenceId,
    };
  },
});
function RouteComponent() {
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const { id } = Route.useParams();
  const {
    initialDataFilters,
    baseDataFilters,
    initialColumnsOrder,
    baseColumnsOrder,
    initialColumnsDisplay,
    baseColumnsDisplay,
    activeSort,
    activePreferenceId,
  } = Route.useLoaderData();

  const csvFileInputRef = useRef<HTMLInputElement>(null);
  const { data: entity } = useQuery(trpc.customEntities.getCustomEntitiesById.queryOptions({ id: id }));
  const { data: views } = useQuery(trpc.customEntities.getCustomViews.queryOptions({ id: id }));

  const [rowFilters, setRowFilters] = useState<Record<string, FilterType>>(() => structuredClone(initialDataFilters));
  const [columnsDisplay, setColumnsDisplay] = useState<Record<string, boolean>>(() =>
    structuredClone(initialColumnsDisplay),
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => structuredClone(initialColumnsOrder));
  const [sort, setSort] = useState<ColumnSort[]>(activeSort);
  const [activePreference, setActivePreference] = useState<string | undefined>(activePreferenceId ?? undefined);
  const [isValueChanged, setIsValueChanged] = useState(false);

  useEffect(() => {
    setRowFilters(structuredClone(initialDataFilters));
    setFilter(getFilterFromRowFilters(structuredClone(initialDataFilters) as any));
    setColumnsDisplay(structuredClone(initialColumnsDisplay));
    setColumnsOrder(structuredClone(initialColumnsOrder));
    setSort(activeSort);
    setActivePreference(activePreferenceId ?? undefined);
    setIsValueChanged(false);
  }, [initialDataFilters, initialColumnsDisplay, initialColumnsOrder, activeSort, activePreferenceId]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [failedRowsDialogOpen, setFailedRowsDialogOpen] = useState(false);
  const [failedRows, setFailedRows] = useState<any[]>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMultiRowsDialogOpen, setDeleteMultiRowsDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentItem, setCurrentItem] = useState({ name: "", id: "" });
  const debouncedFilter = useDebounce(rowFilters, 500);

  const [filter, setFilter] = useState<Record<string, unknown>>(() =>
    getFilterFromRowFilters(structuredClone(initialDataFilters) as any),
  );
  const [page, setPage] = useState({
    size: 100,
    currentPage: 1,
    totalRows: 1,
    tableOffset: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const trimmedSearch = typeof debouncedSearchTerm === "string" ? debouncedSearchTerm.trim() : "";
  const searchParam = trimmedSearch.length > 0 ? trimmedSearch : undefined;

  const { data, status, refetch } = useQuery({
    ...trpc.customEntities.getPaginatedData.queryOptions({
      id: id,
      filters: Object.fromEntries(
        Object.entries(debouncedFilter).map(([key, value]) => {
          const selectedOptions =
            value.multiSelect?.options?.filter((opt) => opt.selected).map((opt) => opt.value) || [];
          return [
            key,
            {
              ...(value.textInput && { textInput: value.textInput.value }),
              ...(value.dateRange && { dateRangeInput: { from: value.dateRange.from, to: value.dateRange.to } }),
              optionsInput: selectedOptions,
            },
          ];
        }),
      ),
      sort: sort,
      pagination: {
        page: page.currentPage,
        pageSize: page.size,
      },
      search: searchParam,
    }),
    enabled: filtersReady,
  });

  const uploadCsv = useMutation(
    trpc.customEntities.uploadCsv.mutationOptions({
      onSuccess: (res) => {
        if (res.length > 0) {
          setFailedRows(res);
          setFailedRowsDialogOpen(true);
          return;
        }
        window.location.reload();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const deleteData = useMutation(
    trpc.customEntities.deleteCustomEntityData.mutationOptions({
      onSuccess: async () => {
        toast.success("Row Deleted successfully");
        setDeleteDialogOpen(false);
        refetch();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const moveColumn = useMutation(
    trpc.data.moveColumn.mutationOptions({
      onSuccess: async (data) => {
        if (data) {
          setColumnsOrder(data);
        }
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const changeColumnDisplay = useMutation(
    trpc.data.changeColumnDisplay.mutationOptions({
      onSuccess: async () => {},
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const stickyColumn = useMutation(
    trpc.data.stickyColumn.mutationOptions({
      onSuccess: async () => {
        refetch();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const deleteMultipleCustomData = useMutation(
    trpc.customEntities.deleteMultipleCustomModelData.mutationOptions({
      onSuccess: async () => {
        toast.success("Deleted successfully");
        setDeleteMultiRowsDialogOpen(false);
        setSelectedRows([]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  useEffect(() => {
    setPage((prev) => {
      if (prev.currentPage === 1 && prev.tableOffset === 0) {
        return prev;
      }
      return {
        ...prev,
        currentPage: 1,
        tableOffset: 0,
      };
    });
  }, [searchParam]);

  useEffect(() => {
    if (data?.total !== page.totalRows && data) {
      setPage((prev) => ({
        ...prev,
        totalRows: data?.total,
      }));
    }
  }, [data?.total]);

  useEffect(() => {
    setFiltersReady(true);
  }, []);

  const filterFields = useMemo<FilterFields>(() => {
    const newList = Object.entries(rowFilters)
      .filter(([key, value]) => value.multiSelect?.active || value.dateRange?.active)
      .map(([key, value]) => {
        return {
          id: key,
          label: value.dateRange?.active
            ? value.dateRange?.label
            : value.multiSelect?.label
              ? value.multiSelect?.label
              : "",
          type: (value.dateRange?.active ? "date-range" : "multi-select") as "date-range" | "multi-select",
          from: value.dateRange?.active ?? "",
          to: value.dateRange?.active ?? "",
          options: value.multiSelect?.active
            ? value.multiSelect.options.map((opi) => ({ label: opi.label, value: opi.value }))
            : [],
        };
      }) as any;

    return newList || [];
  }, [rowFilters]);
  const filterValues = useMemo<ColumnFiltersState>(() => {
    return (
      Object.entries(filter ?? {}).map(([key, value]) => {
        return { id: key, value };
      }) ?? []
    );
  }, [filter]);

  const sortValues = useMemo<ColumnSort[]>(() => {
    return (
      Object.entries(sort ?? {}).map(([key, value]) => {
        return { id: value.id, desc: value.desc };
      }) ?? []
    );
  }, [sort]);

  const columns = useMemo<ColumnDef<any>[]>(() => {
    let tc: ColumnDef<inferOutput<typeof trpc.customEntities.getCustomEntitiesById>>[] = [];
    if (entity) {
      type Attribute = z.infer<typeof attrTypes>[number];
      const attributes = entity.attributes as Attribute[];
      tc = attributes.map((item) => ({
        header: item.name,
        accessorKey: item.id ?? item.name,
        meta: { sticky: Boolean(data?.stickyColumn && item.id === data?.stickyColumn[entity.id]) },
        cell: ({ row }) => {
          const key = item.id ?? item.name;
          const value = (row.original as Record<string, any>)[key];
          if (
            value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === "") ||
            (Array.isArray(value) && value.length === 0)
          ) {
            return <span className="text-muted-foreground">-</span>;
          }

          if (item.type === "Record" && value && typeof value === "object") {
            return (
              <MultiRecordAttributeCell
                value={[
                  {
                    id: (value as any).value,
                    source: item.table_reference || "",
                  },
                ]}
              />
            );
          }
          if (item.type === "Multi Record") {
            if (!value || !value.length) return null;
            return (
              <div className="flex flex-wrap gap-1">
                <MultiRecordAttributeCell
                  mode={value.length > 1 ? "compact" : "default"}
                  value={(value as unknown as Array<{ value: string; entity: string }>).map((v) => ({
                    id: v.value,
                    source: v.entity,
                  }))}
                />
              </div>
            );
          }
          if (item.type === "Select" && typeof value === "string") {
            const opt = item.options?.find((o) => o.value === value);
            return (
              <Link
                to="/$datasetSlug/data/custom/details/$id/table/$customDetailsId"
                params={{ datasetSlug, id: id, customDetailsId: row.original.id }}
              >
                <Badge variant="secondary">{opt?.label || value}</Badge>
              </Link>
            );
          }
          if (item.type === "Multi Select" && Array.isArray(value)) {
            const labels = value.map((v) => {
              const key = String(v);
              return item.options?.find((o) => String(o.value) === key)?.label || key;
            });
            return <CompactBadgeList values={labels} />;
          }
          if (Array.isArray(value)) {
            return <div>{value.join(", ")}</div>;
          }
          if (typeof value === "boolean") {
            return <Switch className="disabled:cursor-default" disabled checked={value} />;
          }

          if (item.type === "Text" && item.richText && typeof value === "string") {
            const fullText = markdownToString(value);
            const truncated = truncateString(fullText, 50);
            return (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <span className="cursor-pointer">{truncated}</span>
                </HoverCardTrigger>
                <HoverCardContent className="max-w-xs whitespace-pre-line max-h-[50vh] overflow-y-auto min-w-[20vw]">
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none [&_img]:!max-w-full [&_img]:!h-auto"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
                  />
                </HoverCardContent>
              </HoverCard>
            );
          }

          if (item.type === "Date" || item.type === "TimeStamp") {
            if (!value || typeof value !== "string") return "-";
            if (item.dateTimeFormat === "age_in_years") return computeAgeInYears(value);
            if (item.dateTimeFormat === "default") return value;
            const jsDate = dayjs(value).toDate();
            return <HumanDateWithTooltip date={jsDate} />;
          }

          if (item.type === "Text") {
            return <>{renderTextWithLinks(value)}</>;
          }

          return (
            <Link
              to="/$datasetSlug/data/custom/details/$id/table/$customDetailsId"
              params={{ datasetSlug, id: id, customDetailsId: row.original.id }}
            >
              {String(value)}
            </Link>
          );
        },
      }));

      tc.push({
        id: "owner",
        accessorKey: "owner",
        header: "Owner",
        cell: ({ row }) => {
          const val = (row.original as any)["owner"];
          return <BeneficiaryCell value={val} linkUser />;
        },
      });

      tc.push({
        id: "created_by",
        accessorKey: "created_by",
        header: "Created By",
        cell: ({ row }) => {
          const val = (row.original as any)["created_by"];
          return <BeneficiaryCell value={val} />;
        },
      });

      tc.push({
        id: "created_at",
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }) => {
          const val = (row.original as any)["created_at"];
          if (!val) return "-";
          return <HumanDateWithTooltip date={new Date(val)} />;
        },
      });

      tc.push({
        id: "updated_at",
        accessorKey: "updated_at",
        header: "Updated At",
        cell: ({ row }) => {
          const val = (row.original as any)["updated_at"];
          if (!val) return "-";
          return <HumanDateWithTooltip date={new Date(val)} />;
        },
      });

      tc.push({
        accessorKey: "Action",
        cell: ({ row }) => (
          <div className="flex">
            <Link
              to="/$datasetSlug/data/custom/details/$id/edit/$rowId"
              params={{ datasetSlug, id: id, rowId: row.original.id }}
            >
              <EditIcon size={20} className="mr-2 text-blue-500 cursor-pointer" />
            </Link>
            <Trash2
              size={20}
              className="mr-2 text-red-500 cursor-pointer"
              onClick={() => {
                setCurrentItem({ name: String(row.original.name), id: String(row.original.id) });
                setDeleteDialogOpen(true);
              }}
            />
          </div>
        ),
      });
    }
    return applyStickyColumnPreference(tc, entity ? data?.stickyColumn?.[entity.id] : undefined);
  }, [entity, data?.stickyColumn, datasetSlug, id]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const { url, id: fileId } = await queryClient.fetchQuery(
        trpc.customEntities.generateUploadUrl.queryOptions({ id: id }),
      );
      const postRes = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "text/csv",
        },
      });
      if (postRes.status === 200) {
        await uploadCsv.mutate({ id: id, fileId: fileId });
      } else {
        console.log(postRes);
        toast.error("Failed to upload file");
      }
    }
  };
  const { updateBreadCrumbs } = useBreadcrumbsContext();

  useEffect(() => {
    if (entity?.name) {
      updateBreadCrumbs([
        {
          title: entity.p_name || entity.name,
          link: `/${location.pathname.split("/")[1]}/data/custom/details/${id}/table`,
        },
      ]);
    }
    return () => {
      updateBreadCrumbs([]);
    };
  }, [entity?.name]);

  return (
    <div className="m-5">
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline">
            <div className="flex gap-1">
              <h1 className="text-2xl font-bold">{entity?.p_name || entity?.name}</h1>
              <span className="self-end font-bold text-lg">({data?.total ?? 0})</span>
            </div>
            <Link className="ml-2" to="/$datasetSlug/data/custom/details/$id/settings" params={{ datasetSlug, id: id }}>
              <SettingsIcon className="w-4 h-4 text-muted-foreground transition hover:rotate-90" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/$datasetSlug/data/custom/details/$id/create" params={{ datasetSlug, id: id }}>
              <Button>
                <PlusIcon /> Add {entity?.name}
              </Button>
            </Link>
          </div>
        </div>
        <div className="flex items-center">
          <TablePreferences
            tableName={String(entity?.id || id)}
            dataFilters={structuredClone(baseDataFilters) as any}
            defaultFilters={structuredClone(baseDataFilters) as any}
            defaultSort={[]}
            currentFilters={rowFilters}
            currentColumnsDisplay={columnsDisplay}
            currentColumnsOrder={columnsOrder} // Fix here: was using data?.entitiesColumnsOrder
            currentSort={sort}
            isValueChanged={isValueChanged}
            setIsValueChanged={setIsValueChanged}
            setActivePreference={(val) => setActivePreference(val ?? undefined)}
            onApplyPreference={(filters, newSort, latestColumns, latestColumnsOrder) => {
              setRowFilters(structuredClone(filters as any));
              setFilter(getFilterFromRowFilters(structuredClone(filters as any)));
              setSort(newSort ?? []);
              setColumnsDisplay(structuredClone(latestColumns || {}));
              setColumnsOrder(structuredClone(latestColumnsOrder || []));
              setIsValueChanged(false);
              setPage((prev) => ({
                ...prev,
                currentPage: 1,
                tableOffset: 0,
              }));
            }}
            onInvalidateData={() => {
              refetch();
            }}
          />
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={`Search ${entity?.p_name?.toLowerCase() || entity?.name?.toLowerCase() || "records"}...`}
            className="pl-9"
          />
        </div>
      </div>
      <Input
        type="file"
        ref={csvFileInputRef}
        className="hidden"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        onChange={handleFileChange}
      />
      <DataTable<any>
        initialColumnVisibility={{
          updated_at: false,
          ...columnsDisplay,
        }}
        data={(data?.data ?? []) as any[]}
        isLoading={status === "pending"}
        showPagination={true}
        pageLimit={page.size}
        pageOffset={page.tableOffset}
        rowCount={page.totalRows}
        showSearchFilter={false}
        showColumnFilter
        columns={reorderColumnsByAccessKeys(columns, columnsOrder as string[] | undefined)}
        rowFilters={rowFilters as any}
        setRowFilters={(update: any) => {
          setRowFilters(update);
          setIsValueChanged(true);
        }}
        manualSorting={true}
        onPageChange={({ pageOffset }) => {
          setPage((prev) => ({
            ...prev,
            tableOffset: pageOffset,
            currentPage: Math.floor(pageOffset / page.size) + 1,
          }));
        }}
        filterFields={filterFields}
        filterValues={filterValues}
        onFilterChange={(filterState) => {
          const curr = Object.fromEntries(filterState.map(({ id, value }) => [id, value])) as Record<string, any>;
          let newFilterFields: Record<string, any> = {};
          for (let item of filterFields) {
            if (item.type === "multi-select") {
              newFilterFields[item.id] = curr[item.id] ?? [];
            }
            if (item.type === "date-range") {
              newFilterFields[item.id] = curr[item.id]
                ? {
                    from: curr[item.id].from,
                    to: curr[item.id].to,
                  }
                : undefined;
            }
          }
          setFilter(newFilterFields);
          let newRowFilter: Record<string, any> = {};
          for (let [key, value] of Object.entries(rowFilters)) {
            let newFilter = value;
            if (newFilter.multiSelect?.active) {
              newFilter.multiSelect.options = newFilter.multiSelect.options.map((optionItem) => {
                return {
                  label: optionItem.label,
                  value: optionItem.value,
                  selected: newFilterFields[key].includes(optionItem.value),
                };
              });
            }
            if (newFilter.dateRange?.active) {
              newFilter.dateRange.from = newFilterFields[key] ? newFilterFields[key].from : "";
              newFilter.dateRange.to = newFilterFields[key] ? newFilterFields[key].to : "";
            }
            newRowFilter[key] = newFilter;
          }
          setRowFilters(newRowFilter);
          setIsValueChanged(true);
        }}
        onSortChange={(sortObject) => {
          setSort(sortObject);
          setIsValueChanged(true);
        }}
        sortValues={sortValues}
        setVisibleColumns={(columns: Record<string, boolean>) => {
          setColumnsDisplay(columns);
          if (!activePreference) {
            changeColumnDisplay.mutate({
              entity: String(entity?.id || id),
              columns,
            });
          } else if (Object.entries(columns).some(([key, value]) => value !== columnsDisplay[key])) {
            setIsValueChanged(true);
          }
        }}
        extendedFunctions={[
          {
            title: "Upload CSV",
            icon: Upload,
            fn: () => {
              csvFileInputRef.current?.click();
            },
          },
          {
            title: "Download Sample",
            icon: Download,
            fn: () => {
              if (entity?.attributes) {
                const blob = new Blob([entity?.attributes.map((item) => item.name).join(", ")], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `sample_${entity.name}_${new Date().toISOString()}.csv`;
                a.click();
              }
            },
          },
        ]}
        moveColumn={(columnName: string, direction: "left" | "right") => {
          if (!activePreference) {
            moveColumn.mutate({
              columns: columnsOrder
                ? reorderColumnsByAccessKeys(columns, columnsOrder as string[]).map(
                    (item) =>
                      (item as { accessorKey?: string; id?: string }).accessorKey || (item as { id?: string }).id || "",
                  )
                : columns.map(
                    (item) =>
                      (item as { accessorKey?: string; id?: string }).accessorKey || (item as { id?: string }).id || "",
                  ),
              entity: String(entity?.id || ""),
              columnName,
              direction,
            });
          } else {
            const newColumnsOrder = columnsSort(
              reorderColumnsByAccessKeys(columns, columnsOrder as string[]).map(
                (item) =>
                  (item as { accessorKey?: string; id?: string }).accessorKey || (item as { id?: string }).id || "",
              ),
              columnName,
              direction,
            );
            setColumnsOrder(newColumnsOrder);
            setIsValueChanged(true);
          }
        }}
        stickyColumn={(columnName, shouldStick) => {
          stickyColumn.mutate({
            entity: String(entity?.id || ""),
            columnName: shouldStick ? columnName : "",
          });
        }}
        multiSelectFunction={[
          <BulkManualAutomationSelector
            recordType="custom_entity"
            entityId={id}
            displayName={entity?.name}
            selectedRows={selectedRows.map((rowId) => ({ id: rowId }))}
          />,
          {
            title: "Delete Selected Rows",
            icon: <Trash2 color="red" />,
            tooltip: "Delete Selected Rows",
            fn: () => {
              setDeleteMultiRowsDialogOpen(true);
            },
          },
        ]}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
      />
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteData.mutate({ id: currentItem.id });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={failedRowsDialogOpen} onOpenChange={setFailedRowsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>CSV uploaded successfully except these items</DialogTitle>
            <DialogDescription>
              <DataTable
                data={failedRows || []}
                columns={[
                  { accessorKey: "error" },
                  {
                    accessorKey: "item",
                    cell: ({ row }: { row: { original: Record<string, any> } }) => {
                      return <div>{JSON.stringify(row.original.item)}</div>;
                    },
                  },
                ]}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFailedRowsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteMultipleRows
        deleteDialogOpen={deleteMultiRowsDialogOpen}
        setDeleteDialogOpen={setDeleteMultiRowsDialogOpen}
        selectedRows={selectedRows}
        deleteFn={deleteMultipleCustomData}
      />
    </div>
  );
}
